/**
 * Contexto de autenticación.
 * - Lee el token desde localStorage al montar
 * - Expone usuario actual + login/logout
 *
 * En producción con Cloudflare Access, el flujo queda transparente:
 * el header lo inyecta Access y el endpoint /api/auth/me devuelve el user.
 */
import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, auth } from '@/services/api';
import type { AuthUser } from '@/lib/types';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = React.createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      if (!auth.token) return { user: null };
      try {
        return await api<{ user: AuthUser }>('/api/auth/me');
      } catch {
        return { user: null };
      }
    },
  });

  const value = React.useMemo<AuthContextValue>(
    () => ({
      user: data?.user ?? null,
      isLoading,
      async login(email: string, password: string) {
        const res = await api<{ token: string; user: AuthUser }>('/api/auth/login', {
          method: 'POST',
          body: { email, password },
        });
        auth.setToken(res.token);
        await refetch();
      },
      async logout() {
        try {
          await api('/api/auth/logout', { method: 'POST' });
        } catch {
          // si falla el endpoint igual cerramos sesión en el cliente
        }
        auth.setToken(null);
        location.href = '/login';
      },
    }),
    [data, isLoading, refetch],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = React.useContext(AuthContext);
  if (!ctx) throw new Error('useAuth fuera de AuthProvider');
  return ctx;
}
